import React, { useState } from 'react';
import {
  TextField,
  Button,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  Box,
  FormControlLabel,
  Switch,
  Typography,
} from '@mui/material';
import QuillTextField from '../../components/common/QuillTextField';
import { useJobContext } from '../../JobContext';
import useJobForm from '../../hooks/useJobForm';
import { formatDateToYYYYMMDD } from '../../utils/job';
import HttpHelper from '../../utils/HttpHelper';
import { useNavigate } from 'react-router-dom';
import { isStringEmpty } from '../../utils/formValidation';
import { normalizeQuill } from '../../utils/quill';
import SelectingAssessments from '../Assessments/SelectingAssessments';
import { useAssessmentManager } from '../../hooks/UseAssessmentManager';

const CREATE_JOB_ENDPOINT: string = 'jobs/create';

const CreateJob: React.FC = () => {
  const {
    title,
    setJobTitle,
    description,
    setJobDescription,
    requirements,
    setJobRequirements,
    responsibilities,
    setJobResponsibilities,
    remote,
    setRemote,
    city,
    setCity,
    province,
    setProvince,
    country,
    setCountry,
    published,
    currentlyAvailable,
    salary,
    setSalary,
    selectedAssessment,
    setSelectedAssessment,
    jobCategory,
    jobType,
    applicationDeadline,
    setApplicationDeadline,
    handleJobCategoryChange,
    handleJobTypeChange,
    handleCurrentlyAvailableChange,
    handleRemoteChange,
    handlePublishedChange,
  } = useJobForm();

  const { jobCategoryMapping } = useJobContext();
  const { jobTypeMapping } = useJobContext();
  const navigate = useNavigate();

  const [titleFieldEmpty, setTitleFieldEmpty] = useState<boolean>(false);
  const [descriptionFieldEmpty, setDescriptionFieldEmpty] = useState<boolean>(false);
  const [responsibilitiesFieldEmpty, setResponsibilitiesFieldEmpty] = useState<boolean>(false);
  const [requirementsFieldEmpty, setRequirementsFieldEmpty] = useState<boolean>(false);
  const [cityFieldEmpty, setCityFieldEmpty] = useState<boolean>(false);
  const [provinceFieldEmpty, setProvinceFieldEmpty] = useState<boolean>(false);
  const [countryFieldEmpty, setCountryFieldEmpty] = useState<boolean>(false);
  const [salaryFieldEmpty, setSalaryFieldEmpty] = useState<boolean>(false);
  const [jobTypeFieldEmpty, setJobTypeFieldEmpty] = useState<boolean>(false);
  const [jobCategoryFieldEmpty, setJobCategoryFieldEmpty] = useState<boolean>(false);
  const [applicationDeadlineFieldEmpty, setApplicationDeadlineFieldEmpty] = useState<boolean>(false);

  const {
    assessmentsList,
    selectedAssessments,
    searchQuery,
    handleSearch,
    handleSelectAssessment,
    handleSearchChange,
    handleDrop,
    handleDragStart,
    handleDragOver,
  } = useAssessmentManager();

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    const isTitleEmpty = isStringEmpty(title);
    const isDescriptionEmpty = isStringEmpty(description);
    const isResponsibilitiesEmpty = isStringEmpty(responsibilities);
    const isRequirementsEmpty = isStringEmpty(requirements);
    const isCityEmpty = isStringEmpty(city);
    const isProvinceEmpty = isStringEmpty(province);
    const isCountryEmpty = isStringEmpty(country);
    const isSalaryEmpty = isStringEmpty(salary);
    const isJobTypeEmpty = isStringEmpty(jobType);
    const isJobCategoryEmpty = isStringEmpty(jobCategory);
    const isDeadlineEmpty = isStringEmpty(applicationDeadline);

    setTitleFieldEmpty(isTitleEmpty);
    setDescriptionFieldEmpty(isDescriptionEmpty);
    setResponsibilitiesFieldEmpty(isResponsibilitiesEmpty);
    setRequirementsFieldEmpty(isRequirementsEmpty);
    setCityFieldEmpty(isCityEmpty);
    setProvinceFieldEmpty(isProvinceEmpty);
    setCountryFieldEmpty(isCountryEmpty);
    setSalaryFieldEmpty(isSalaryEmpty);
    setJobTypeFieldEmpty(isJobTypeEmpty);
    setJobCategoryFieldEmpty(isJobCategoryEmpty);
    setApplicationDeadlineFieldEmpty(isDeadlineEmpty);

    if (
      !isTitleEmpty &&
      !isDescriptionEmpty &&
      !isResponsibilitiesEmpty &&
      !isRequirementsEmpty &&
      !isCityEmpty &&
      !isProvinceEmpty &&
      !isCountryEmpty &&
      !isSalaryEmpty &&
      !isJobTypeEmpty &&
      !isJobCategoryEmpty &&
      !isDeadlineEmpty
    ) {
      const numericJobType = jobTypeMapping[jobType] || 0;
      const numericJobCategory = jobCategoryMapping[jobCategory] || 0;
      const currentDate = new Date();
      const formattedDatePosted = formatDateToYYYYMMDD(currentDate);

      const jobData = {
        job: {
          title,
          description: normalizeQuill(description),
          requirements: normalizeQuill(requirements),
          responsibilities: normalizeQuill(responsibilities),
          remote,
          city,
          province,
          country,
          published,
          salary: Number(salary),
          datePosted: formattedDatePosted,
          jobType: numericJobType,
          jobCategory: numericJobCategory,
          currentlyAvailable,
          applicationDeadline,
        },
      };

      const responseData = await HttpHelper.post(CREATE_JOB_ENDPOINT, jobData);
      const newJobId = responseData.jobId;

      // Link assessments if any were selected
      if (selectedAssessments.length > 0) {
        const assessmentIds = selectedAssessments.map((a: any) => a.id);
        await HttpHelper.put(`jobs/${newJobId}/assessments/add`, { assessmentIds });
      }

      navigate('/manage-jobs');
    }
  };

  return (
    <Box
      component="form"
      onSubmit={handleSubmit}
      sx={{ width: '100%' }}
    >
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'flex-end',
          alignItems: 'center',
          marginTop: 2,
        }}
      >
        <Typography variant="h5" fontWeight="bold">
          Create a New Job
        </Typography>

        <FormControlLabel
          control={
            <Switch
              checked={published}
              onChange={handlePublishedChange}
              name="published"
              sx={{
                '& .MuiSwitch-switchBase.Mui-checked': {
                  color: 'var(--ttg-green)',
                  '&:hover': { backgroundColor: 'var(--button-hover-green)' },
                },
                '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                  backgroundColor: 'var(--ttg-green)',
                },
              }}
            />
          }
          label="Published"
          labelPlacement="start"
          sx={{ marginLeft: 'auto' }}
        />
      </Box>

      <TextField
        label="Job Title"
        variant="outlined"
        value={title}
        onChange={e => setJobTitle(e.target.value)}
        fullWidth
        margin="normal"
        required
        error={titleFieldEmpty}
      />

      <Box sx={{ display: 'flex', gap: 2, marginBottom: 2 }}>
        <FormControl fullWidth margin="normal" required error={jobCategoryFieldEmpty}>
          <InputLabel id="job-category-label">Job Category</InputLabel>
          <Select
            labelId="job-category-label"
            value={jobCategory}
            label="Job Category"
            onChange={handleJobCategoryChange}
          >
            {Object.keys(jobCategoryMapping).map(category => (
              <MenuItem key={category} value={category}>{category}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl fullWidth margin="normal" required error={jobTypeFieldEmpty}>
          <InputLabel id="job-type-label">Job Type</InputLabel>
          <Select
            labelId="job-type-label"
            value={jobType}
            label="Job Type"
            onChange={handleJobTypeChange}
          >
            {Object.keys(jobTypeMapping).map(type => (
              <MenuItem key={type} value={type}>{type}</MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      <Box sx={{ display: 'flex', gap: 2, marginBottom: 2 }}>
        <TextField
          label="City"
          variant="outlined"
          value={city}
          onChange={e => setCity(e.target.value)}
          fullWidth
          margin="normal"
          required
          error={cityFieldEmpty}
        />
        <TextField
          label="Province"
          variant="outlined"
          value={province}
          onChange={e => setProvince(e.target.value)}
          fullWidth
          margin="normal"
          required
          error={provinceFieldEmpty}
        />
        <TextField
          label="Country"
          variant="outlined"
          value={country}
          onChange={e => setCountry(e.target.value)}
          fullWidth
          margin="normal"
          required
          error={countryFieldEmpty}
        />
      </Box>

      <Box sx={{ display: 'flex', gap: 2, marginBottom: 2 }}>
        <TextField
          label="Application Deadline"
          variant="outlined"
          value={applicationDeadline}
          onChange={e => setApplicationDeadline(e.target.value)}
          type="date"
          InputLabelProps={{ shrink: true }}
          fullWidth
          margin="normal"
          required
          error={applicationDeadlineFieldEmpty}
        />
      </Box>

      <Box sx={{ display: 'flex', gap: 2, marginBottom: 2 }}>
        <TextField
          label="Salary"
          variant="outlined"
          type="number"
          value={salary}
          onChange={e => setSalary(e.target.value)}
          fullWidth
          margin="normal"
          sx={{ flexGrow: 1 }}
          InputProps={{ inputProps: { min: 0 } }}
          required
          error={salaryFieldEmpty}
        />
        <FormControl fullWidth margin="normal">
          <InputLabel id="availability-label">Availability</InputLabel>
          <Select
            labelId="availability-label"
            value={currentlyAvailable ? 'currently-available' : 'future-opening'}
            label="Availability"
            onChange={handleCurrentlyAvailableChange}
            fullWidth
          >
            <MenuItem value="currently-available">Currently Available</MenuItem>
            <MenuItem value="future-opening">Future Opening</MenuItem>
          </Select>
        </FormControl>
        <FormControlLabel
          control={
            <Switch
              checked={remote}
              onChange={handleRemoteChange}
              name="remote"
              sx={{
                '& .MuiSwitch-switchBase.Mui-checked': {
                  color: 'var(--ttg-green)',
                  '&:hover': { backgroundColor: 'var(--button-hover-green)' },
                },
                '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                  backgroundColor: 'var(--ttg-green)',
                },
              }}
            />
          }
          label="Remote"
          labelPlacement="start"
          sx={{ marginLeft: 'auto' }}
        />
      </Box>

      <QuillTextField
        label="Job Description"
        value={description}
        onChange={setJobDescription}
        error={descriptionFieldEmpty}
        minHeight={200}
        required={true}
      />
      <QuillTextField
        label="Responsibilities"
        value={responsibilities}
        onChange={setJobResponsibilities}
        error={responsibilitiesFieldEmpty}
        minHeight={200}
        required={true}
      />
      <QuillTextField
        label="Requirements"
        value={requirements}
        onChange={setJobRequirements}
        error={requirementsFieldEmpty}
        minHeight={200}
        required={true}
      />

      <SelectingAssessments
        assessmentsList={assessmentsList}
        selectedAssessments={selectedAssessments}
        searchQuery={searchQuery}
        handleSearch={handleSearch}
        handleSelectAssessment={handleSelectAssessment}
        handleSearchChange={handleSearchChange}
        handleDrop={handleDrop}
        handleDragStart={handleDragStart}
        handleDragOver={handleDragOver}
        handleSubmit={handleSubmit}
      />

      <Button
        type="submit"
        variant="contained"
        sx={{
          backgroundColor: 'var(--ttg-green)',
          '&:hover': { backgroundColor: 'var(--button-hover-green)' },
          marginTop: 3,
          marginBottom: 3,
        }}
      >
        Create Job
      </Button>
    </Box>
  );
};

export default CreateJob;
